import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, IconButton, Tabs, Tab,
  Box, Typography, CircularProgress, Alert, Chip, Avatar, Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import HistoryIcon from '@mui/icons-material/History';
import api from '../../services/api';

/**
 * VedicToolsOverlay — Modal overlay showing user details, Vedic tools, and history.
 *
 * During active call:
 * - Fetches user profile, kundlis, and consultation history
 * - Auto-populates Vedic tools with user's birth data
 * - Astrologer can switch between tabs: Profile, Tools, History
 */
const VedicToolsOverlay = ({ open, onClose, consultationId }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [userProfile, setUserProfile] = useState(null);
  const [kundlis, setKundlis] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Tool results (auto-generated)
  const [toolResults, setToolResults] = useState({});
  const [generatingTools, setGeneratingTools] = useState(false);

  useEffect(() => {
    if (open && consultationId) {
      loadUserData();
    }
  }, [open, consultationId]);

  const loadUserData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [profileRes, kundlisRes, historyRes] = await Promise.all([
        api.get(`/consultations/${consultationId}/user-profile`),
        api.get(`/consultations/${consultationId}/user-kundlis`),
        api.get(`/consultations/${consultationId}/user-history`),
      ]);

      const profile = profileRes.data.data;
      setUserProfile(profile);
      setKundlis(kundlisRes.data.data?.kundlis || []);
      setHistory(historyRes.data.data?.consultations || []);

      // Auto-generate Vedic tool data using user's birth details
      if (profile?.date_of_birth && profile?.time_of_birth && profile?.place_of_birth) {
        autoGenerateTools(profile);
      }
    } catch (err) {
      console.error('Failed to load user data:', err);
      setError('Failed to load user data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const autoGenerateTools = async (profile) => {
    setGeneratingTools(true);
    const birthData = {
      name: profile.name,
      date_of_birth: profile.date_of_birth,
      time_of_birth: profile.time_of_birth,
      place_of_birth: profile.place_of_birth,
    };

    const results = {};

    // Fire all tool APIs in parallel (best-effort each)
    const toolCalls = [
      { key: 'kundli', call: () => api.post('/free-kundli', birthData) },
      { key: 'kalasakthi', call: () => api.post('/kalasakthi/v2', birthData) },
      { key: 'dailyBalance', call: () => api.post('/daily-balance', birthData) },
    ];

    await Promise.allSettled(
      toolCalls.map(async ({ key, call }) => {
        try {
          const res = await call();
          results[key] = res.data;
        } catch (err) {
          results[key] = { error: err.message };
        }
      })
    );

    setToolResults(results);
    setGeneratingTools(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric', month: 'long', year: 'numeric',
    });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return 'N/A';
    return new Date(timeStr).toLocaleTimeString('en-IN', {
      hour: '2-digit', minute: '2-digit',
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6">
          {userProfile ? `${userProfile.name} — User Details` : 'User Details'}
        </Typography>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <>
            <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 2 }}>
              <Tab icon={<PersonIcon />} label="Profile" />
              <Tab icon={<AutoAwesomeIcon />} label="Vedic Tools" />
              <Tab icon={<HistoryIcon />} label="History" />
            </Tabs>

            {/* Profile Tab */}
            {activeTab === 0 && userProfile && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Avatar
                    src={userProfile.profile_image}
                    sx={{ width: 64, height: 64 }}
                  >
                    {userProfile.name?.[0]}
                  </Avatar>
                  <Box>
                    <Typography variant="h6">{userProfile.name}</Typography>
                    <Typography variant="body2" color="text.secondary">{userProfile.email}</Typography>
                    {userProfile.phone && (
                      <Typography variant="body2" color="text.secondary">{userProfile.phone}</Typography>
                    )}
                  </Box>
                </Box>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Date of Birth</Typography>
                    <Typography>{formatDate(userProfile.date_of_birth)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Time of Birth</Typography>
                    <Typography>{formatTime(userProfile.time_of_birth)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Place of Birth</Typography>
                    <Typography>{userProfile.place_of_birth || 'N/A'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Gender</Typography>
                    <Typography>{userProfile.gender || 'N/A'}</Typography>
                  </Box>
                </Box>

                {kundlis.length > 0 && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Saved Kundlis ({kundlis.length})</Typography>
                    {kundlis.map((k) => (
                      <Chip
                        key={k.id}
                        label={`${k.name} — ${formatDate(k.date_of_birth)}`}
                        sx={{ mr: 1, mb: 1 }}
                        variant="outlined"
                      />
                    ))}
                  </>
                )}
              </Box>
            )}

            {/* Vedic Tools Tab */}
            {activeTab === 1 && (
              <Box>
                {generatingTools ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
                    <CircularProgress size={24} />
                    <Typography>Auto-generating Vedic data for {userProfile?.name}...</Typography>
                  </Box>
                ) : !userProfile?.date_of_birth ? (
                  <Alert severity="warning">
                    User has not provided birth details. Vedic tools cannot be auto-populated.
                  </Alert>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* Kundli */}
                    <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                      <Typography variant="subtitle1" fontWeight="bold">Kundli (Birth Chart)</Typography>
                      {toolResults.kundli?.error ? (
                        <Alert severity="error" sx={{ mt: 1 }}>{toolResults.kundli.error}</Alert>
                      ) : toolResults.kundli ? (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          Chart generated successfully. Ascendant and planetary positions available.
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Loading...</Typography>
                      )}
                    </Box>

                    {/* KalaSakthi */}
                    <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                      <Typography variant="subtitle1" fontWeight="bold">KalaSakthi</Typography>
                      {toolResults.kalasakthi?.error ? (
                        <Alert severity="error" sx={{ mt: 1 }}>{toolResults.kalasakthi.error}</Alert>
                      ) : toolResults.kalasakthi ? (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          KalaSakthi data generated for today.
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Loading...</Typography>
                      )}
                    </Box>

                    {/* Daily Balance */}
                    <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                      <Typography variant="subtitle1" fontWeight="bold">Daily Balance</Typography>
                      {toolResults.dailyBalance?.error ? (
                        <Alert severity="error" sx={{ mt: 1 }}>{toolResults.dailyBalance.error}</Alert>
                      ) : toolResults.dailyBalance ? (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          Daily planetary balance calculated.
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Loading...</Typography>
                      )}
                    </Box>
                  </Box>
                )}
              </Box>
            )}

            {/* History Tab */}
            {activeTab === 2 && (
              <Box>
                {history.length === 0 ? (
                  <Typography color="text.secondary">No previous consultations with this user.</Typography>
                ) : (
                  history.map((c) => (
                    <Box
                      key={c.id}
                      sx={{ p: 2, mb: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="subtitle2">
                          {c.type} consultation — {c.status}
                        </Typography>
                        <Chip label={`${c.duration || 0} min`} size="small" />
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(c.created_at)}
                        {c.rating > 0 && ` — Rating: ${c.rating}/5`}
                      </Typography>
                      {c.review && (
                        <Typography variant="body2" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                          "{c.review}"
                        </Typography>
                      )}
                    </Box>
                  ))
                )}
              </Box>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default VedicToolsOverlay;
